import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Dialog,
  DialogContent,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  contactName,
  level,
  phoneFax,
  secondaryPhone,
  emailContact,
  description,
} from '../../config/contactFormDataConfig';
import { RenderFields, RenderFieldsDropdown } from './RenderFields';
import { addContact } from '../../services/contactService';
import useContactFormData from '../../hooks/useContactFormData';
import ContactAccordion from './ContactAccordion';

const AddContactModal = ({ open, handleClose, handleDataChange, isLoading }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { formData, handleChange, validationErrors, resetForm } = useContactFormData();

  if (isLoading) {
    return (
      <Dialog open={open} onClose={handleClose}>
        <DialogContent>Loading contact details...</DialogContent>
      </Dialog>
    );
  }

  const isFormValid = () => {
    return (
      Object.values(validationErrors).every((error) => !error) &&
      formData.contactName &&
      formData.phone1 &&
      formData.emailAddress
    );
  };

  const handleModalClose = () => {
    resetForm(); // Reset form when closing the modal
    handleClose();
  };

  const handleAddContact = async () => {
    if (!isFormValid()) {
      alert('Please fix validation errors before saving.');
      return;
    }
    try {
      await addContact(formData);
      resetForm();
      handleClose();
      handleDataChange();
    } catch (error) {
      console.error('Error adding contact:', error);
      alert('Error adding contact');
    }
  };
  

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth={false}
      PaperProps={{
        style: {
          width: isExpanded ? '90%' : '1000px',
          maxHeight: '90vh',
          minHeight: '90vh',
          overflow: 'visible',
        },
      }}
      fullWidth={true}
      style={{ zIndex: 1400 }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white z-10">
        <div className="flex justify-between items-center p-4">
          <div className="flex items-center space-x-4 flex-grow">
            <button
              onClick={handleClose}
              className="p-2 rounded-full hover:bg-gray-200 transition"
              aria-label="Back"
            >
              <ArrowBackIcon />
            </button>
            <h2 className="text-2xl font-bold text-left">Add Contact</h2>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full hover:bg-gray-200 transition"
            aria-label={isExpanded ? 'Shrink Modal' : 'Expand Modal'}
          >
            {isExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </button>
        </div>
        <div className="flex space-x-4 p-4">
          <button
            onClick={handleAddContact}
            className="px-6 py-2 bg-yellow-500 font-bold rounded-md hover:bg-yellow-600 text-sss-blue-sel transition duration-300 ease-in-out"
          >
            Save
          </button>
        </div>
      </div>
      <DialogContent dividers>
        <div className="space-y-4">
          <div className="flex flex-col gap-4">
            {/* Contact Name Section Accordion */}
            <ContactAccordion label="General">
              <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-2`}>
                <div className="w-1/2">
                  <RenderFields
                    fields={contactName}
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </div>
                <div className="w-1/2">
                  <RenderFields
                    fields={description}
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                  <RenderFieldsDropdown
                    fields={level}
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </div>
              </div>
            </ContactAccordion>

            {/* Contact Phone/Email Section Accordion */}
            <ContactAccordion label="Contact Info">
              <div className={`flex ${isExpanded ? 'flex-row' : 'flex-col md:flex-row'} gap-2`}>
                <div className="w-1/2">
                  <RenderFields
                    fields={phoneFax}
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </div>
                <div className="w-1/2">
                  <RenderFields
                    fields={secondaryPhone}
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                  {isExpanded && (
                    <RenderFields
                      fields={emailContact}
                      formData={formData}
                      handleChange={handleChange}
                      validationErrors={validationErrors}
                    />
                  )}
                </div>
              </div>
              {!isExpanded && (
                <div className="overflow-hidden gap-2">
                  <RenderFields
                    fields={emailContact}
                    formData={formData}
                    handleChange={handleChange}
                    validationErrors={validationErrors}
                  />
                </div>
              )}
            </ContactAccordion>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


AddContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleDataChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default AddContactModal;
